import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  TablePagination,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import axios from "axios";
import { toast } from "react-toastify";
import { SearchOutlined } from "@mui/icons-material";

const baseUrl = process.env.REACT_APP_API_PATH;

const statusColors = {
  accepted: "#286a94",
  rejected: "#d32f2f",
  requested: "#0288d1",
  pending: "#f9a825",
};

const AdminScheduledInterviews = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [rejectReason, setRejectReason] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rejectError, setRejectError] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [rejectInterviewId, setRejectInterviewId] = useState(null);
  const open = Boolean(anchorEl);
  const [filters, setFilters] = useState({
    interviewStatus: "",
    candidateAcceptanceStatus: "",
    availabilityRequested: "",
    search: "",
  });
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setPage(0);
    }, 1000);

    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    fetchData();
  }, [
    page,
    rowsPerPage,
    filters.interviewStatus,
    filters.candidateAcceptanceStatus,
    filters.availabilityRequested,
    debouncedSearch,
  ]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        size: rowsPerPage,
      };

      if (filters?.interviewStatus) {
        params.interviewStatus = filters.interviewStatus;
      }

      if (filters?.candidateAcceptanceStatus) {
        params.candidateAcceptanceStatus = filters.candidateAcceptanceStatus;
      }

      if (filters.availabilityRequested !== "") {
        params.availabilityRequested = filters.availabilityRequested === "true";
      }

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      const resp = await axios.get(
        `${baseUrl}/ebench/interview/admin-approval_v2`,
        { params }
      );

      const pageData = resp?.data?.data;

      if (pageData?.content) {
        setRows(pageData.content);
        setTotal(pageData.totalElements);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching interviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (row) => {
    if (row.isAcceptedByAdmin) return "ACCEPTED BY ADMIN";
    if (row.isAcceptedByVendor) return "ACCEPTED BY VENDOR";
    if (row.isAcceptedByCandidate === "REJECTED") return "CANDIDATE REJECTED";
    if (row.isAcceptedByCandidate === "ACCEPTED") return "CANDIDATE ACCEPTED";
    if (row.availabilityRequested) return "REQUESTED";
    return "PENDING ACTION";
  };

  const getStatusColor = (row) => {
    if (row.isAcceptedByAdmin) return statusColors.accepted;
    if (row.isAcceptedByCandidate === "ACCEPTED") return statusColors.accepted;
    if (row.isAcceptedByCandidate === "REJECTED") return statusColors.rejected;
    if (row.availabilityRequested) return statusColors.requested;
    return statusColors.pending;
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleAcceptInterview = async (e) => {
    e.preventDefault();
    if (!selectedRow) return;
    try {
      await axios.post(
        `${baseUrl}/ebench/admin/interview/${selectedRow.interviewId}/accept`
      );
      toast.success("Interview accepted successfully");
      handleMenuClose();
      fetchData();
    } catch (error) {
      console.error("Accept interview failed", error);
      toast.error(
        error?.response?.data?.status?.message || "Failed to accept interview"
      );
    }
  };

  const handleRejectInterview = async () => {
    if (!rejectInterviewId) return;
    const reason = rejectReason?.trim();
    console.log(reason);
    if (!reason) {
      toast.error("Reject reason is required");
      return;
    }
    try {
      await axios.post(
        `${baseUrl}/ebench/admin/interview/${rejectInterviewId}/reject`,
        {
          reason: rejectReason,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Interview Rejected Successfully!");
      setOpenRejectDialog(false);
      setRejectReason("");
      setRejectInterviewId(null);
      fetchData();
    } catch (error) {
      console.error("Reject interview failed", error);
      toast.error(
        error?.response?.data?.status?.message || "Failed to reject interview"
      );
    }
  };

  const handleCandidateAvailability = async () => {
    if (!selectedRow?.interviewId) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_API_PATH}/ebench/admin/interview/${selectedRow.interviewId}/ask-candidate-availability`
      );
      handleMenuClose();
      toast.success("Candidate availability requested successfully");
      fetchData();
    } catch (error) {
      console.error("Failed to ask candidate availability", error);

      toast.error(
        error?.response?.data?.status?.message ||
          "Failed to request candidate availability"
      );
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const formatDuration = (duration) => {
    if (!duration) return "-";

    const [hours, minutes] = duration.split(":").map(Number);

    if (hours > 0 && minutes > 0) {
      return `${hours} hr ${minutes} mins`;
    }

    if (hours > 0) {
      return `${hours} hr`;
    }

    return `${minutes} mins`;
  };

  const formatTime = (time) => {
    if (!time) return "-";

    const [hours, minutes] = time.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;

    return `${formattedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  /* ---------------- UI ---------------- */
  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Typography
        variant="h5"
        fontWeight={700}
        mb={2}
        sx={{ color: "#286a94" }}
      >
        Interview Invitations
      </Typography>

      {/* 🔹 Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          backgroundColor: "#fafcff",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search interviews..."
              value={filters.search}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  search: e.target.value,
                }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined sx={{ color: "#64748b" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {/* Interview Status */}
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Interview Status</InputLabel>
              <Select
                label="Interview Status"
                value={filters.interviewStatus}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    interviewStatus: e.target.value,
                  }))
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Candidate Acceptance Status */}
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Candidate Status</InputLabel>
              <Select
                label="Candidate Status"
                value={filters.candidateAcceptanceStatus}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    candidateAcceptanceStatus: e.target.value,
                  }))
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="ACCEPTED">Accepted</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Availability Requested */}
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Availability</InputLabel>
              <Select
                label="Availability"
                value={filters.availabilityRequested}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    availabilityRequested: e.target.value,
                  }))
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Requested</MenuItem>
                <MenuItem value="false">Not Requested</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Clear Filters */}
          <Grid item xs={12} sm={12} md={3} textAlign="left">
            <Button
              variant="outlined"
              size="small"
              sx={{
                mt: { xs: 1, md: 0 },
                color: "#286a94",
                borderColor: "#286a94",
                "&:hover": {
                  borderColor: "#1f5474",
                  backgroundColor: "rgba(40, 106, 148, 0.08)",
                },
              }}
              onClick={() => {
                setFilters({
                  interviewStatus: "",
                  candidateAcceptanceStatus: "",
                  availabilityRequested: "",
                  search: "",
                });
                setPage(0);
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} sx={{ color: "#286a94" }} />
          </Box>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ textTransform: "uppercase" }}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Candidate Name
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Vendor Name
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Interview Type
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Interview Status
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Cancellation Reason
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Interview Date
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Interview Time
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Interview Mode
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Interview Duration
                  </TableCell>

                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Candidate Status
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    Admin Status
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>Actions</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.interviewId} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {row.candidateName}
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {row.vendorName}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.interviewType}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",

                          backgroundColor: (() => {
                            const type = row.interviewType?.toUpperCase();
                            if (type?.includes("TECH")) return "#e6f0f8";
                            if (type?.includes("HR")) return "#fdf2e9";
                            return "#f3f4f6";
                          })(),

                          "& .MuiChip-label": {
                            color: (() => {
                              const type = row.interviewType?.toUpperCase();
                              if (type?.includes("TECH")) return "#286a94";
                              if (type?.includes("HR")) return "#9a3412";
                              return "#374151";
                            })(),
                            fontWeight: 600,
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.interviewStatus}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          backgroundColor: (() => {
                            const status = row.interviewStatus?.toUpperCase();
                            if (status === "SCHEDULED") return "#e6f0f8";
                            if (status === "COMPLETED") return "#e6f4ea";
                            if (status === "CANCELLED") return "#fdecea";
                            return "#f3f4f6";
                          })(),

                          "& .MuiChip-label": {
                            color: (() => {
                              const status = row.interviewStatus?.toUpperCase();
                              if (status === "SCHEDULED") return "#286a94";
                              if (status === "COMPLETED") return "#2e7d32";
                              if (status === "CANCELLED") return "#c62828";
                              return "#374151";
                            })(),
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.reason || ""} arrow placement="top">
                        <span>{row.reason}</span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>{row.interviewDate?.split(" ")[0]}</TableCell>

                    <TableCell>{formatTime(row.interviewStartTime)}</TableCell>

                    <TableCell>
                      <Chip
                        label={
                          row.interviewMode === "REMOTE"
                            ? "Remote"
                            : row.interviewMode === "IN_PERSON"
                            ? "Offline"
                            : row.interviewMode
                        }
                        size="small"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",

                          backgroundColor: (() => {
                            const mode = row.interviewMode?.toUpperCase();
                            if (mode === "REMOTE") return "#ede7f6";
                            if (mode === "IN_PERSON") return "#fff3e0";
                            return "#f3f4f6";
                          })(),

                          "& .MuiChip-label": {
                            color: (() => {
                              const mode = row.interviewMode?.toUpperCase();
                              if (mode === "REMOTE") return "#6a1b9a";
                              if (mode === "IN_PERSON") return "#ef6c00";
                              return "#374151";
                            })(),
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {formatDuration(row.duration)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={getStatusLabel(row)}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(row),
                          color: "#fff",
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.isAcceptedByAdmin ? "Accepted" : "Pending"}
                        size="small"
                        sx={{
                          textTransform: "uppercase",
                          fontWeight: 600,
                          backgroundColor: row.isAcceptedByAdmin
                            ? "#e6f4ea"
                            : "#fff7ed",
                          "& .MuiChip-label": {
                            color: row.isAcceptedByAdmin
                              ? "#2e7d32"
                              : "#b45309",
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No interviews found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* ---------------- PAGINATION ---------------- */}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <Dialog
        open={openRejectDialog}
        onClose={() => {
          setOpenRejectDialog(false);
          setRejectReason("");
          setRejectError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#286a94" }}>
          Reject Interview
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Please provide a reason for rejecting this interview.
          </Typography>

          <TextField
            label="Reject Reason"
            placeholder="Enter reason for rejection"
            fullWidth
            multiline
            minRows={3}
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.target.value);
              setRejectError("");
            }}
            error={!!rejectError}
            helperText={rejectError}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setOpenRejectDialog(false);
              setRejectReason("");
              setRejectError("");
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#286a94",
              "&:hover": {
                backgroundColor: "#1f5677",
              },
            }}
            onClick={handleRejectInterview}
          >
            Submit Rejection
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- ACTION MENU ---------------- */}
      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        {/* Cancelled */}
        {selectedRow?.interviewStatus === "CANCELLED" && (
          <MenuItem disabled>
            <NotificationsActiveOutlinedIcon sx={{ mr: 1 }} />
            Interview Cancelled
          </MenuItem>
        )}

        {/* Completed */}
        {selectedRow?.interviewStatus === "COMPLETED" && (
          <MenuItem disabled>
            <NotificationsActiveOutlinedIcon sx={{ mr: 1 }} />
            Interview Completed
          </MenuItem>
        )}

        {/* Confirmed by Admin */}
        {selectedRow?.interviewStatus === "SCHEDULED" &&
          selectedRow?.isAcceptedByAdmin && (
            <MenuItem disabled>
              <NotificationsActiveOutlinedIcon sx={{ mr: 1 }} />
              Interview Confirmed
            </MenuItem>
          )}

        {/* Ask Candidate Availability */}
        {selectedRow?.interviewStatus === "SCHEDULED" &&
          selectedRow?.isAcceptedByCandidate === "PENDING" &&
          !selectedRow?.isAcceptedByAdmin &&
          !selectedRow?.availabilityRequested && (
            <MenuItem onClick={handleCandidateAvailability}>
              <EventAvailableOutlinedIcon sx={{ mr: 1 }} />
              Ask Candidate Availability
            </MenuItem>
          )}

        {/* Availability already requested */}
        {selectedRow?.interviewStatus === "SCHEDULED" &&
          selectedRow?.isAcceptedByCandidate === "PENDING" &&
          !selectedRow?.isAcceptedByVendor &&
          !selectedRow?.isAcceptedByAdmin &&
          selectedRow?.availabilityRequested && (
            <MenuItem disabled>
              <EventAvailableOutlinedIcon sx={{ mr: 1 }} />
              Availability Already Requested
            </MenuItem>
          )}

        {selectedRow?.interviewStatus === "SCHEDULED" &&
          selectedRow?.isAcceptedByVendor &&
          !selectedRow?.isAcceptedByAdmin && (
            <>
              <MenuItem onClick={handleAcceptInterview}>
                <CheckCircleOutlineIcon sx={{ mr: 1, color: "success.main" }} />
                Accept Interview
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setRejectInterviewId(selectedRow?.interviewId);
                  handleMenuClose();
                  setOpenRejectDialog(true);
                }}
              >
                <CancelOutlinedIcon sx={{ mr: 1, color: "error.main" }} />
                Reject Interview
              </MenuItem>
            </>
          )}
      </Menu>
    </Box>
  );
};

export default AdminScheduledInterviews;
